/**
 * Control (faculty): sign in, list sessions, set up a new one, then run it.
 */
import { useEffect, useRef, useState } from 'react';
import { PACINGS, PRESETS, type PresetKey } from '@nightshift/engine/client';
import type { Route } from '../app/router.ts';
import { linkProps, navigate } from '../app/router.ts';
import { api } from '../net/api.ts';
import { Fold, Moon } from '../ui/bits.tsx';
import { Replay } from './Replay.tsx';
import { DebriefPrint } from './DebriefPrint.tsx';
import { CohortComparison } from './Cohort.tsx';
import { CohortAssignment, CohortControls } from './CohortControls.tsx';
import type { Cohort } from './cohort-summary.ts';
import { rememberLinks, SessionScreen } from './SessionScreen.tsx';
import type { SessionRow } from './wire.ts';

export function FacultyApp({ route }: { route: Route }) {
  const [me, setMe] = useState<'unknown' | 'in' | 'out'>('unknown');
  useEffect(() => {
    api<{ faculty: string | null }>('/api/me')
      .then((r) => setMe(r.faculty ? 'in' : 'out'))
      .catch(() => setMe('out'));
  }, []);
  if (me === 'unknown') return <main className="fac-loading muted">Opening Control…</main>;
  if (me === 'out') return <Login onIn={() => setMe('in')} />;
  const [, sub, id, extra] = route.parts;
  if (sub === 'cohorts' && id) return <CohortComparison key={id} id={id} currentSession={route.query.get('session')} />;
  if (sub === 's' && id && extra === 'print') return <DebriefPrint key={id} sessionId={id} table={route.query.get('table')} />;
  if (sub === 's' && id && extra === 'replay') return <Replay sessionId={id} />;
  if (sub === 's' && id) return <SessionScreen key={id} url={`/ws?session=${encodeURIComponent(id)}`} sessionId={id} readonly={false} />;
  return <Sessions />;
}

function Login({ onIn }: { onIn: () => void }) {
  const [pw, setPw] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <main className="fac-login">
      <form
        className="fac-login-card"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setErr(null);
          try {
            await api('/api/faculty/login', { body: { password: pw } });
            onIn();
          } catch (x) {
            setErr((x as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <Moon size={40} />
        <h1 className="display">Control</h1>
        <p className="muted">Faculty run the room from here: the clock, the events, and every table’s hidden cards.</p>
        <div className="field">
          <label htmlFor="pw">Faculty password</label>
          <input id="pw" className="input" type="password" autoComplete="current-password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="fac-password" />
        </div>
        <button className="btn btn-primary btn-block" disabled={busy || !pw} data-testid="fac-login">
          Sign in
        </button>
        {err && (
          <p className="form-error" role="alert">
            {err}
          </p>
        )}
      </form>
    </main>
  );
}

function Sessions() {
  const [rows, setRows] = useState<SessionRow[] | null>(null);
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const refresh = useRef(0);
  const load = () => {
    const request = ++refresh.current;
    return Promise.all([api<SessionRow[]>('/api/sessions'), api<Cohort[]>('/api/cohorts')])
      .then(([sessions, groups]) => {
        if (request !== refresh.current) return;
        setRows(sessions); setCohorts(groups); setErr(null);
      })
      .catch((e) => {
        console.warn('Control list loading failed', e);
        if (request === refresh.current) setErr((e as Error).message);
      });
  };
  useEffect(() => {
    void load();
    return () => { refresh.current++; };
  }, []);
  return (
    <div className="fac-page">
      <header className="fac-top">
        <span className="fac-brand">
          <Moon size={24} /> Control
        </span>
        <button
          className="btn btn-quiet btn-small"
          onClick={async () => {
            await api('/api/faculty/logout', { body: {} }).catch(() => {});
            location.reload();
          }}
        >
          Sign out
        </button>
      </header>
      <div className="fac-sessions">
        <div style={{ display: 'grid', gap: 24 }}>
          <NewSession cohorts={cohorts} />
          <CohortControls cohorts={cohorts} onCreated={load} />
        </div>
        <section className="fac-panel">
          <h2 className="display">Sessions</h2>
          {err && <p className="form-error">{err}</p>}
          {rows && rows.length === 0 && <p className="empty">No sessions yet. Set one up on the left.</p>}
          {rows && rows.length > 0 && (
            <table className="fac-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Code</th>
                  <th>Preset</th>
                  <th>Tables</th>
                  <th>Status</th>
                  <th>Cohort</th>
                  <th>Created</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <a {...linkProps(`/control/s/${r.id}`)}>{r.name}</a>
                    </td>
                    <td className="mono">{r.code}</td>
                    <td>{PRESETS[r.preset as PresetKey]?.label ?? r.preset}</td>
                    <td>{r.tables}</td>
                    <td>
                      <span className={`status status-${r.status}`}>{r.status.toLowerCase()}</span>
                    </td>
                    <td><CohortAssignment session={r} cohorts={cohorts} onAssigned={load} /></td>
                    <td className="muted">{new Date(r.createdAt).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })}</td>
                    <td className="row">
                      <a className="btn btn-small" {...linkProps(`/control/s/${r.id}`)}>
                        Open
                      </a>
                      {r.startedAt && (
                        <a className="btn btn-small btn-quiet" {...linkProps(`/control/s/${r.id}/replay`)}>
                          Replay
                        </a>
                      )}
                      <button
                        className="btn btn-small btn-quiet"
                        onClick={async () => {
                          if (!window.confirm(`Delete “${r.name}” and its log? This cannot be undone.`)) return;
                          await api(`/api/sessions/${r.id}`, { method: 'DELETE' }).catch((e) => setErr((e as Error).message));
                          void load();
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </div>
  );
}

function NewSession({ cohorts }: { cohorts: Cohort[] }) {
  const [name, setName] = useState('Night shift');
  const [preset, setPreset] = useState<PresetKey>('ORIENTATION');
  const [tables, setTables] = useState(2);
  const [pacing, setPacing] = useState<'STANDARD' | 'NINETY' | 'TEACHING'>('STANDARD');
  const [layers, setLayers] = useState({ staff: false, vents: false, blood: false });
  const [seed, setSeed] = useState('');
  const [cohortId, setCohortId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const pace = PACINGS[pacing];
  const minutes = Math.round((pace.scriptTurns.length * (pace.arrivalsMs + pace.huddleMs + pace.actMs + pace.tickMs + pace.logMs) + pace.handoverMs) / 60000);
  return (
    <form
      className="fac-panel new-session"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr(null);
        try {
          const r = await api<{ id: string; spectatorToken: string; projectorToken: string }>('/api/sessions', { body: { name: name.trim() || 'Night shift', preset, tables, pacing: { key: pacing }, layers, seed: seed.trim() || undefined, cohort_id: cohortId || null } });
          rememberLinks(r.id, { spectatorToken: r.spectatorToken, projectorToken: r.projectorToken, spoilers: false });
          navigate(`/control/s/${r.id}`);
        } catch (x) {
          setErr((x as Error).message);
        } finally {
          setBusy(false);
        }
      }}
    >
      <h2 className="display">New session</h2>
      <div className="ns-col">
        <div className="field">
          <label htmlFor="ns-name">Name</label>
          <input id="ns-name" className="input" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} data-testid="ns-name" />
        </div>
        <fieldset className="field">
          <legend>Preset</legend>
          <div className="preset-pick">
            {(Object.keys(PRESETS) as PresetKey[]).map((k) => (
              <label key={k} className={`preset ${preset === k ? 'on' : ''}`}>
                <input type="radio" name="preset" value={k} checked={preset === k} onChange={() => setPreset(k)} />
                <strong>{PRESETS[k].label}</strong>
                <span className="muted">{PRESETS[k].blurb}</span>
              </label>
            ))}
          </div>
        </fieldset>
      </div>
      <div className="ns-col">
        <div className="field-row">
          <div className="field">
            <label htmlFor="ns-tables">Tables</label>
            <input id="ns-tables" className="input" type="number" min={1} max={12} value={tables} onChange={(e) => setTables(Math.max(1, Math.min(12, Number(e.target.value) || 1)))} data-testid="ns-tables" />
          </div>
          <div className="field">
            <label htmlFor="ns-pacing">Pacing</label>
            <select id="ns-pacing" className="input" value={pacing} onChange={(e) => setPacing(e.target.value as typeof pacing)}>
              <option value="STANDARD">Standard, ten turns</option>
              <option value="NINETY">Ninety minutes, seven turns</option>
              <option value="TEACHING">Teaching, ten slower turns</option>
            </select>
          </div>
        </div>
        <Fold title="Optional layers" hint={(['staff', 'vents', 'blood'] as const).filter((k) => layers[k]).map((k) => ({ staff: 'Staff', vents: 'Ventilators', blood: 'Blood bank' })[k]).join(', ') || 'none'} testId="ns-layers">
          <div className="layer-pick" role="group" aria-label="Optional layers">
            {(
              [
                ['staff', 'Staff', 'Each bed needs Staff tokens; Reds and ICU need two.'],
                ['vents', 'Ventilators', 'A shared pool per table for ICU patients who need one.'],
                ['blood', 'Blood bank', 'Some Theatre steps use units; order more and wait.'],
              ] as const
            ).map(([k, label, text]) => (
              <label key={k} className={`layer ${layers[k] ? 'on' : ''}`}>
                <input type="checkbox" checked={layers[k]} onChange={(e) => setLayers({ ...layers, [k]: e.target.checked })} data-testid={`layer-${k}`} />
                <strong>{label}</strong>
                <span className="muted">{text}</span>
              </label>
            ))}
          </div>
        </Fold>
        <details className="field">
          <summary className="muted">Seed (to replay the same night)</summary>
          <input className="input" value={seed} maxLength={40} onChange={(e) => setSeed(e.target.value)} placeholder="Leave blank for a fresh night" />
        </details>
        <div className="field">
          <label htmlFor="ns-cohort">Cohort</label>
          <select id="ns-cohort" data-testid="ns-cohort" className="input" value={cohortId} onChange={(event) => setCohortId(event.target.value)}>
            <option value="">No cohort</option>
            {cohorts.map((cohort) => <option key={cohort.id} value={cohort.id}>{cohort.name}</option>)}
          </select>
        </div>
        <section className="ns-summary" aria-live="polite" data-testid="ns-summary">
          <h3 className="sec-title">This room</h3>
          <p>
            <strong>
              {tables} table{tables > 1 ? 's' : ''}, {tables * 4} to {tables * 5} people
            </strong>{' '}
            (bots fill any gaps). About {minutes} minutes of play, then the debrief.
          </p>
          <p className="muted">
            {PRESETS[preset].label}: {PRESETS[preset].blurb}
            {(['staff', 'vents', 'blood'] as const).some((k) => layers[k]) ? ` Layers: ${(['staff', 'vents', 'blood'] as const).filter((k) => layers[k]).map((k) => ({ staff: 'Staff', vents: 'Ventilators', blood: 'Blood bank' })[k]).join(', ')}.` : ''}
          </p>
        </section>
        <button className="btn btn-primary btn-block" disabled={busy} data-testid="ns-create">
          Set up the room
        </button>
      </div>
      {err && (
        <p className="form-error" role="alert">
          {err}
        </p>
      )}
    </form>
  );
}
